import React from 'react';
import { CheckCircleOutlined, StarFilled, StarOutlined, CheckOutlined, MailOutlined } from '@ant-design/icons';
import {
  Row, Badge, Popover, Button,
} from 'antd';

import sortByString, { arrayIncludesTerm } from '../helpers/helpers';
import FormApprovalButton, {
  APPROVE_FORM_SUBMISSION, REJECT_FORM_SUBMISSION,
} from './FormApprovalButton';
import FormMoreButton from './CompletedForms/FormMoreButton';
import SearchFormFilterHeader from './CompletedForms/SearchFormFilterHeader';
import {
  getFormattedCurrency,
  renderPreloadedIntoColumn,
  renderProjectColumn,
  MANUAL_GLOBAL_TAB,
} from './formHelpers';
import MultiValuePopoverColumn from '../common/tables/MultiValuePopoverColumn';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import OntraccrTag from '../common/OntraccrTag';
import FormStatusTags from './FormStatusTags';
import { onPOCloseModal } from '../payables/invoices/invoiceHelpers';
import TableFilterDropdown from '../common/tables/TableFilterDropdown';
import Permissions from '../auth/Permissions';
import { sortByObjProperty } from '../common/helpers';

const STATE_FILTERS = [
  { text: 'Assigned', value: 'Assigned' },
  { text: 'Draft', value: 'Draft' },
  { text: 'Editing', value: 'Editing' },
];

const multiValueSorter = (valueMap, key) => (a, b) => {
  const aValues = a[key] || [];
  const bValues = b[key] || [];
  const aNames = aValues.map((value) => valueMap[value]?.name).join('') || '';
  const bNames = bValues.map((value) => valueMap[value]?.name).join('') || '';
  return aNames.localeCompare(bNames);
};

const getPoValueColumn = (type = 'PO') => ({
  title: `${type} Value`,
  dataIndex: 'formValue',
  key: 'formValue',
  align: 'center',
  width: type === 'Sub-Contract' ? 180 : 140,
  render: getFormattedCurrency,
  sorter: (a, b) => a.formValue - b.formValue,
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const costToDateColumn = {
  title: 'Cost to Date',
  dataIndex: 'costToDate',
  key: 'costToDate',
  align: 'center',
  width: 140,
  render: getFormattedCurrency,
  sorter: (a, b) => a.costToDate - b.costToDate,
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
};

const committedCostColumn = {
  title: 'Committed Cost',
  dataIndex: 'committedCost',
  key: 'committedCost',
  align: 'center',
  width: 140,
  render: (_, record) => {
    const committedCost = record.formValue - record.costToDate;

    return getFormattedCurrency(committedCost);
  },
};

const getPOClosedColumn = (onPOCloseClick, isPO = false) => ({
  title: 'Closed',
  dataIndex: 'isClosed',
  key: 'isClosed',
  align: 'center',
  width: 150,
  showSorterTooltip: false,
  filters: [
    {
      text: 'Closed',
      value: 'Closed',
    },
    {
      text: 'Not Closed',
      value: 'Not Closed',
    },
  ],
  onFilter: (value, record) => {
    if (value === 'Closed') return record.isClosed;

    return !record.isClosed;
  },
  filterDropdown: TableFilterDropdown,
  render: (val, record) => {
    if (!val) {
      const {
        id: formId,
        number,
        templateName,
      } = record ?? {};

      const nameSuffix = number ? ` - ${number}` : '';
      const formName = `${templateName}${nameSuffix}`;

      const onPOClose = (
        onPOCloseModal({
          formName,
          formId,
          onPOCloseClick,
          setPopoverVisible: null,
          isPO,
        })
      );

      return <OnTraccrButton title="Close" onClick={onPOClose} />;
    }

    return <CheckCircleOutlined style={{ color: 'green' }} />;
  },
});

const getApprovalColumn = ({
  range, templates, templateSectionPermissionMap,
}) => (
  {
    title: null,
    dataIndex: 'approval',
    key: 'approval',
    align: 'center',
    width: 230,
    render: (_, record) => {
      const { formId, templateId } = record;
      const responses = record?.data?.fields?.reduce((acc, field = {}) => {
        const {
          fieldId,
          response,
        } = field;

        return {
          ...acc,
          [fieldId]: response,
        };
      }, {});

      const sections = templates[templateId]?.sections || [];
      const sectionPermissionMap = templateSectionPermissionMap[templateId] || {};

      return record.status !== 'Cancelled'
        ? (
          <Row justify="center" align="middle" gutter={16}>
            <FormApprovalButton
              formId={formId}
              type={REJECT_FORM_SUBMISSION}
              range={range}
              isViewMode
              responses={responses}
              sections={sections}
              sectionPermissionMap={sectionPermissionMap}
              shouldCalculateErrors
              style={{
                borderRadius: 10,
              }}
              showIcon
            />
            <FormApprovalButton
              formId={formId}
              type={APPROVE_FORM_SUBMISSION}
              range={range}
              isViewMode
              responses={responses}
              sections={sections}
              sectionPermissionMap={sectionPermissionMap}
              shouldCalculateErrors
              style={{
                borderRadius: 10,
              }}
              showIcon
            />
          </Row>
        )
        : null;
    },
    fixed: 'left',
  }
);

const getUsersColumn = (userFilters, userMap) => (
  {
    title: 'User(s)',
    dataIndex: 'userId',
    exportDataIndex: 'userNames',
    key: 'userId',
    align: 'center',
    width: 200,
    filters: userFilters,
    filterSearch: true,
    onFilter: (value, { users } = {}) => users.includes(value),
    filterDropdown: SearchFormFilterHeader,
    render: (_, record) => (
      <MultiValuePopoverColumn
        valueMap={userMap}
        record={record}
        type="users"
      />
    ),
    sorter: multiValueSorter(userMap, 'users'),
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
  }
);

const getTemplateNameColumn = ({
  sorter, sortDirection, filters, onFilter,
}) => {
  const useSort = sorter && sortDirection;
  const useFilter = filters && onFilter;
  const templateNameCol = {
    title: 'Form',
    dataIndex: 'templateName',
    key: 'templateName',
    align: 'center',
    width: 150,
  };

  if (useSort) {
    templateNameCol.sorter = sorter;
    templateNameCol.showSorterTooltip = false;
    templateNameCol.sortDirections = sortDirection;
  }

  if (useFilter) {
    templateNameCol.filters = filters;
    templateNameCol.onFilter = onFilter;
    templateNameCol.filterDropdown = TableFilterDropdown;
  }

  return templateNameCol;
};

const getNumberColumn = () => (
  {
    title: 'Number',
    dataIndex: 'number',
    key: 'number',
    align: 'center',
    width: 150,
    sorter: sortByObjProperty('number'),
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
  }
);

const getVendorColumn = (vendorFilters, vendorMap) => (
  {
    title: 'Vendor(s)',
    key: 'vendors',
    align: 'center',
    width: 150,
    filters: vendorFilters,
    filterSearch: true,
    onFilter: (value, { vendors = [] } = {}) => arrayIncludesTerm(vendors, value),
    filterDropdown: SearchFormFilterHeader,
    render: (_, record) => (
      <MultiValuePopoverColumn
        valueMap={vendorMap}
        record={record}
        type="vendors"
      />
    ),
    sorter: multiValueSorter(vendorMap, 'vendors'),
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
  }
);

const getCustomerColumn = (t, customerFilters) => (
  {
    title: t('Customer'),
    dataIndex: 'customer',
    align: 'center',
    width: 150,
    filters: customerFilters,
    onFilter: (value, record) => record?.customers?.includes(value),
    filterDropdown: SearchFormFilterHeader,
    render: (val) => (
      <Popover
        placement="top"
        content={<div style={{ width: 250 }}>{val}</div>}
      >
        <div>{val}</div>
      </Popover>
    ),
    sorter: (a, b) => a.customer?.localeCompare(b.customer),
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
  }
);

const getProjectColumn = (
  t,
  projectFilters,
  projectIdMap = {},
  settings = {},
  isManual = false,
) => (
  {
    title: t('Project', { count: 2 }),
    dataIndex: 'project',
    align: 'center',
    width: 150,
    filters: projectFilters,
    filterDropdown: SearchFormFilterHeader,
    onFilter: (value, record) => (
      isManual
        ? (
          record?.projectId === value
          || record?.project === value
        )
        : record.projects?.includes(value)
    ),
    render: (val, record) => {
      const { projects = [] } = record;
      if (isManual) return record.project;
      return renderProjectColumn({
        projects,
        projectIdMap,
        settings,
      });
    },
  }
);

const getCardColumn = ({ cardFilters, onCardColumnClick }) => (
  {
    title: 'Card',
    dataIndex: 'card',
    align: 'center',
    width: 150,
    filters: cardFilters,
    filterDropdown: SearchFormFilterHeader,
    onFilter: (value, record) => record.card === value,
    sorter: sortByObjProperty('card'),
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
    render: (val, record) => {
      if (record.cardId && record.boardId && onCardColumnClick) {
        return (
          <Button
            type="button"
            className="link-button"
            style={{ width: '100%' }}
            onClick={(e) => {
              onCardColumnClick?.(record);
              e.stopPropagation();
            }}
          >
            <span className="link-button-text">
              {val}
            </span>
          </Button>
        );
      }
      return val;
    },
  }
);

const getCreatedOnColumn = () => (
  {
    title: 'Created On',
    dataIndex: 'createdAtDate',
    key: 'createdAtDate',
    align: 'center',
    width: 200,
    sorter: (a, b) => a.createdAt - b.createdAt,
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
  }
);

const getFavoriteColumn = (onClick) => (
  {
    title: '',
    key: 'favorite',
    dataIndex: 'isFavorite',
    render: (isFavorite, record) => (
      isFavorite
        ? <StarFilled onClick={(e) => onClick(e, record)} style={{ fontSize: 16, color: '#F6722B' }} />
        : <StarOutlined onClick={(e) => onClick(e, record)} style={{ fontSize: 16 }} />
    ),
    width: 30,
    align: 'center',
  }
);

const getLastUpdatedColumn = () => (
  {
    title: 'Last Updated',
    dataIndex: 'lastUpdatedDate',
    key: 'lastUpdatedDate',
    align: 'center',
    width: 200,
    sorter: (a, b) => a.lastUpdated - b.lastUpdated,
    showSorterTooltip: false,
    sortDirections: ['descend', 'ascend'],
  }
);

const getStateColumn = () => (
  {
    title: 'State',
    dataIndex: 'state',
    key: 'state',
    align: 'center',
    width: 100,
    filters: STATE_FILTERS,
    onFilter: (value, record) => record.state === value,
    filterDropdown: TableFilterDropdown,
  }
);
const getStatusColumn = (statusFilters) => (
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (val, record) => <FormStatusTags status={val} record={record} />,
    align: 'center',
    width: 150,
    filters: statusFilters,
    onFilter: (value, record) => record.statusId === value,
    filterDropdown: TableFilterDropdown,
  }
);

const getReadColumn = (handleToggleReadStatus, loadingStates = {}) => ({
  title: '',
  dataIndex: 'isRead',
  key: 'isRead',
  fixed: 'left',
  render: (isRead, record) => {
    const loading = loadingStates[record.id] || false;
    const onlyInFormDrafts = record.isDraft;
    const handleToggle = async (e) => {
      e.stopPropagation();
      return handleToggleReadStatus(record.id, !isRead, onlyInFormDrafts);
    };

    const popoverText = isRead ? 'Mark form as unread' : 'Mark form as read';
    const icon = isRead ? <CheckOutlined /> : <MailOutlined />;

    return (
      <Popover
        content={popoverText}
        placement="bottom"
        trigger="hover"
      >
        <OnTraccrButton
          onClick={handleToggle}
          loading={loading}
          disabled={loading}
          title={icon}
          style={{
            width: '20px',
            height: '100%',
            border: 'none',
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#000',
          }}
        />
      </Popover>
    );
  },
  align: 'center',
  width: 40,
});

const getMoreColumn = ({
  isApproval,
  onAddInvoiceClick,
  onPOClose,
  onCopy,
  assignedTemplateSet,
  onHideAssignedFormClick,
}) => {
  const renderCallback = (_, record) => {
    const recordRef = record;
    if (isApproval) recordRef.id = record.formId; // if approval form, adjust id to be form id
    const isNotCompleted = record.status !== 'completed' && record.status !== 'Cancelled' && !record.isClosed;
    const shouldShowPayableOptions = !record.isClosed && (record.type === 'PO' || record.type === 'Sub-Contract');
    const canCopy = (
      onCopy && assignedTemplateSet?.has(record?.templateId)
      && (Permissions.has?.('FORMS_COPY_SUBMISSION') || record?.users?.includes?.(Permissions.id))
    );

    const shouldShow = record.hasQuickBooksInvoice
      || isNotCompleted
      || shouldShowPayableOptions
      || canCopy;

    return shouldShow
      ? (
        <FormMoreButton
          form={record}
          onAddInvoiceClick={onAddInvoiceClick}
          onPOClose={onPOClose}
          onCopy={canCopy ? onCopy : null}
          onHideAssignedFormClick={onHideAssignedFormClick}
        />
      )
      : null;
  };

  return {
    title: null,
    dataIndex: 'hasQuickBooksInvoice',
    render: renderCallback,
    align: 'center',
    width: 25,
  };
};

const getApprovalStatusColumn = (formApprovals) => (
  {
    title: null,
    dataIndex: 'approval',
    key: 'approval',
    render: (_, record) => ((record.id in formApprovals && record.status !== 'Cancelled') ? <Badge status="error" /> : null),
    align: 'center',
    width: 50,
  }
);

const getPreloadedIntoColumn = (onFormClick) => ({
  title: 'Preloaded Into',
  dataIndex: 'preloadedInto',
  exportDataIndex: 'preloadedIntoText',
  key: 'preloadedInto',
  align: 'center',
  width: 200,
  render: (preloadedInto) => renderPreloadedIntoColumn({
    preloadedInto,
    onFormClick,
  }),
});

export const getFormLogColumns = ({
  userMap,
  vendorMap,
  projectIdMap,
  templateFilters,
  formApprovals,
  statusFilters,
  userFilters,
  vendorFilters,
  customerFilters,
  projectFilters,
  cardFilters,
  query,
  isPayable,
  onAddInvoiceClick,
  onPOClose,
  settings,
  t,
  isPO,
  onCopy,
  assignedTemplateSet,
  onCardColumnClick,
  onHideAssignedFormClick,
  onFormClick,
}) => {
  const {
    customerId: queryCustomer,
    projectId: queryProject,
    cardId: queryCard,
    type: formType,
  } = query || {};
  const onFilter = (value, record) => record.templateName === value;

  let cols = [
    getUsersColumn(userFilters, userMap),
    getTemplateNameColumn({
      filters: templateFilters,
      onFilter,
      sorter: sortByString('templateName'),
      sortDirection: ['descend', 'ascend'],
    }),
    getNumberColumn(),
  ];

  if (isPayable) {
    cols.push(getPoValueColumn(formType));
    cols.push(costToDateColumn);
    cols.push(committedCostColumn);
    cols.push(getPOClosedColumn(onPOClose, isPO));
    cols.push(getVendorColumn(vendorFilters, vendorMap));
  }
  if (!queryCustomer && !queryProject) {
    cols.push(getCustomerColumn(t, customerFilters));
  }
  if (!queryProject) {
    cols.push(getProjectColumn(t, projectFilters, projectIdMap, settings));
  }

  if (!queryCard) {
    cols.push(getCardColumn({ cardFilters, onCardColumnClick }));
  }

  if (isPO) {
    cols.push(getPreloadedIntoColumn(onFormClick));
  }

  cols = cols.concat([
    getCreatedOnColumn(),
    getLastUpdatedColumn(),
    getStatusColumn(statusFilters),
    getMoreColumn({
      isApproval: false,
      onAddInvoiceClick,
      onPOClose,
      onCopy,
      assignedTemplateSet,
      onHideAssignedFormClick,
    }),
    getApprovalStatusColumn(formApprovals),
  ]);
  return cols;
};

export const getApproveFormColumns = ({
  projectFilters,
  customerFilters,
  usersFilters,
  cardFilters,
  statusFilters,
  userMap,
  range,
  projectIdMap,
  settings,
  t,
  templates,
  templateSectionPermissionMap,
  onCardColumnClick,
  handleToggleReadStatus,
  loadingStates,
}) => [
  getReadColumn(handleToggleReadStatus, loadingStates),
  getApprovalColumn({
    range, templates, templateSectionPermissionMap, settings,
  }),
  getTemplateNameColumn({ sorter: sortByString('templateName'), sortDirection: ['descend', 'ascend'] }),
  getUsersColumn(usersFilters, userMap),
  getNumberColumn(),
  getCustomerColumn(t, customerFilters),
  getProjectColumn(t, projectFilters, projectIdMap, settings),
  getCardColumn({ cardFilters, onCardColumnClick }),
  getCreatedOnColumn(),
  getLastUpdatedColumn(),
  getStatusColumn(statusFilters),
  getMoreColumn({ isApproval: true }),
];

export const getManualFormColumns = ({
  projectFilters,
  tab,
  onFavoriteClick,
  projectIdMap,
  settings,
  t,
}) => {
  const arr = [
    getTemplateNameColumn({ sorter: sortByString('templateName'), sortDirection: ['descend', 'ascend'] }),
  ];
  if (tab !== MANUAL_GLOBAL_TAB) {
    arr.push(getProjectColumn(t, projectFilters, projectIdMap, settings, true));
  }
  arr.push(getCreatedOnColumn());
  arr.push(getFavoriteColumn(onFavoriteClick));
  return arr;
};

export const getAssignedFormColumns = ({
  projectFilters,
  customerFilters,
  cardFilters,
  projectIdMap,
  settings,
  onCardColumnClick,
  t,
  handleToggleReadStatus,
  loadingStates,
}) => [
  getReadColumn(handleToggleReadStatus, loadingStates),
  getTemplateNameColumn({ sorter: sortByString('templateName'), sortDirection: ['descend', 'ascend'] }),
  getNumberColumn(),
  getProjectColumn(t, projectFilters, projectIdMap, settings),
  getCustomerColumn(t, customerFilters),
  getCardColumn({ cardFilters, onCardColumnClick }),
  getStateColumn(),
  getLastUpdatedColumn(),
];

const getFormsHomeStateColumn = () => ({
  title: 'State',
  key: 'state',
  align: 'center',
  width: 140,
  render: (_, record) => {
    const isDraft = record.state === 'Draft' || record.isDraft;
    const showAssigned = record.state === 'Assigned' || isDraft;
    return (
      <div className="forms-home-state-tags">
        {showAssigned && <OntraccrTag color="blue">Assigned</OntraccrTag>}
        {isDraft && <OntraccrTag color="purple">Draft</OntraccrTag>}
      </div>
    );
  },
});

const getFormsHomeLastEditedColumn = () => ({
  title: 'Last Edited',
  dataIndex: 'lastUpdatedDate',
  key: 'lastUpdatedDate',
  align: 'center',
  width: 160,
});

export const getFormsHomeLogColumns = ({
  projectFilters,
  customerFilters,
  projectIdMap,
  statusFilters,
  settings,
  t,
}) => [
  getTemplateNameColumn({}),
  getNumberColumn(),
  getProjectColumn(t, projectFilters, projectIdMap, settings),
  getCustomerColumn(t, customerFilters),
  getStatusColumn(statusFilters),
  getLastUpdatedColumn(),
];

export const getFormsHomeApproveColumns = ({
  usersFilters,
  customerFilters,
  projectFilters,
  userMap,
  projectIdMap,
  settings,
  t,
  templates,
  templateSectionPermissionMap,
  range,
}) => [
  getTemplateNameColumn({}),
  getUsersColumn(usersFilters, userMap),
  getNumberColumn(),
  getCustomerColumn(t, customerFilters),
  getProjectColumn(t, projectFilters, projectIdMap, settings),
  getApprovalColumn({
    range, templates, templateSectionPermissionMap,
  }),
];

export const getFormsHomeAssignedColumns = ({
  projectFilters,
  customerFilters,
  cardFilters,
  projectIdMap,
  settings,
  onCardColumnClick,
  t,
}) => [
  getTemplateNameColumn({}),
  getNumberColumn(),
  getProjectColumn(t, projectFilters, projectIdMap, settings),
  getCustomerColumn(t, customerFilters),
  getCardColumn({ cardFilters, onCardColumnClick }),
  getFormsHomeStateColumn(),
  getLastUpdatedColumn(),
];

export const getFormsHomeDraftColumns = ({
  projectFilters,
  customerFilters,
  projectIdMap,
  settings,
  t,
  onContinue,
}) => [
  getTemplateNameColumn({}),
  getNumberColumn(),
  getProjectColumn(t, projectFilters, projectIdMap, settings),
  getCustomerColumn(t, customerFilters),
  {
    title: 'Source',
    dataIndex: 'source',
    key: 'source',
    align: 'center',
    width: 140,
  },
  getFormsHomeLastEditedColumn(),
];
